import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Timer, Settings, X, Check, Loader2, Droplets, Coffee,
  Moon, Sun, Flame, CalendarDays, ChevronDown, Zap,
  ShieldCheck, Brain, HeartPulse, Apple, BanIcon, GlassWater
} from 'lucide-react';

const HERO_IMG = 'https://images.pexels.com/photos/31479875/pexels-photo-31479875.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const PROTOCOLS = [
  { key: '16:8', label: '16:8', fasting: 16, eating: 8, desc: 'Standard' },
  { key: '14:10', label: '14:10', fasting: 14, eating: 10, desc: 'Debutant' },
  { key: '18:6', label: '18:6', fasting: 18, eating: 6, desc: 'Avance' },
  { key: '20:4', label: '20:4', fasting: 20, eating: 4, desc: 'Guerrier' },
];

function computeLocalFastingStatus(settings) {
  if (!settings || !settings.is_active) return { is_active: false, in_fasting_window: false };
  const now = new Date();
  const fastH = settings.fasting_hours || 16;
  const eatH = settings.eating_hours || 8;
  const startH = settings.start_hour ?? 20;
  const startM = settings.start_minute ?? 0;
  const todayStart = new Date(now);
  todayStart.setHours(startH, startM, 0, 0);
  const cycleStart = now < todayStart ? new Date(todayStart.getTime() - 86400000) : todayStart;
  const elapsedMs = now.getTime() - cycleStart.getTime();
  const elapsedH = elapsedMs / 3600000;
  const fastEnd = new Date(cycleStart.getTime() + fastH * 3600000);
  const eatEnd = new Date(fastEnd.getTime() + eatH * 3600000);
  if (elapsedH < fastH) {
    return { is_active: true, in_fasting_window: true, phase: 'fasting', progress_pct: Math.round(Math.min(100, (elapsedH / fastH) * 100) * 10) / 10, time_remaining_minutes: Math.max(0, Math.round((fastEnd - now) / 60000)), protocol: settings.protocol || '16:8', fasting_hours: fastH, eating_hours: eatH };
  }
  const eatElapsed = elapsedH - fastH;
  return { is_active: true, in_fasting_window: false, phase: 'eating', progress_pct: Math.round(Math.min(100, (eatElapsed / eatH) * 100) * 10) / 10, time_remaining_minutes: Math.max(0, Math.round((eatEnd - now) / 60000)), protocol: settings.protocol || '16:8', fasting_hours: fastH, eating_hours: eatH };
}

function CircularProgress({ progress, size = 180, strokeWidth = 10, phase, timeText, subText }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          <linearGradient id="fp-fast-g" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a78bfa" /><stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
          <linearGradient id="fp-eat-g" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#6ee7b7" /><stop offset="100%" stopColor="#10b981" />
          </linearGradient>
        </defs>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-white/5" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={`url(#${phase === 'fasting' ? 'fp-fast-g' : 'fp-eat-g'})`} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} className="transition-all duration-700 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-black tracking-tight">{timeText}</span>
        <span className="text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5">{subText}</span>
      </div>
    </div>
  );
}

function AccordionItem({ icon: Icon, iconColor, title, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border/50 rounded-xl overflow-hidden bg-card/40 backdrop-blur-sm" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02] transition-colors">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }}>
            <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FastingPage() {
  const { language, t } = useLanguage();
  const fr = language === 'fr';
  const [settings, setSettings] = useState(null);
  const [localStatus, setLocalStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [streak, setStreak] = useState(null);
  const [formProtocol, setFormProtocol] = useState('16:8');
  const [formFastingH, setFormFastingH] = useState(16);
  const [formEatingH, setFormEatingH] = useState(8);
  const [formStartHour, setFormStartHour] = useState(20);
  const [formStartMin, setFormStartMin] = useState(0);
  const [formActive, setFormActive] = useState(false);
  const timerRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, streakRes] = await Promise.all([
        api.get('/fasting/settings'),
        api.get('/fasting/streak').catch(() => ({ data: { streak: 0, history: [] } })),
      ]);
      const s = settingsRes.data.settings;
      setSettings(s);
      setFormProtocol(s.protocol || '16:8');
      setFormFastingH(s.fasting_hours || 16);
      setFormEatingH(s.eating_hours || 8);
      setFormStartHour(s.start_hour ?? 20);
      setFormStartMin(s.start_minute ?? 0);
      setFormActive(s.is_active || false);
      setStreak(streakRes.data);
      setLocalStatus(computeLocalFastingStatus(s));
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Assistant IA deep-link: /fasting?action=start|stop or ?hours=16
  const [aiActionApplied, setAiActionApplied] = useState(false);
  useEffect(() => {
    if (!settings || aiActionApplied) return;
    const sp = new URLSearchParams(window.location.search);
    const action = sp.get('action');
    const hours = sp.get('hours');
    if (!action && !hours) return;

    if (hours) {
      const h = parseInt(hours, 10);
      if (![12, 14, 16, 18, 20, 24].includes(h)) return;
      setFormFastingH(h);
      setFormEatingH(24 - h);
      setFormProtocol(`${h}:${24 - h}`);
      setShowSettings(true);
    }
    if (action === 'start') {
      setFormActive(true);
      setShowSettings(true);
    } else if (action === 'stop') {
      setFormActive(false);
      setShowSettings(true);
    }
    setAiActionApplied(true);
    // Clean URL
    const next = new URLSearchParams(sp);
    ['action', 'hours'].forEach(k => next.delete(k));
    const newSearch = next.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
  }, [settings, aiActionApplied]);

  useEffect(() => {
    if (!settings?.is_active) return;
    timerRef.current = setInterval(() => setLocalStatus(computeLocalFastingStatus(settings)), 1000);
    return () => clearInterval(timerRef.current);
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/fasting/settings', {
        is_active: formActive, protocol: formProtocol,
        fasting_hours: formFastingH, eating_hours: formEatingH,
        start_hour: formStartHour, start_minute: formStartMin,
      });
      setShowSettings(false);
      toast.success(t('pages.fasting_saved'));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  const handleProtocolSelect = (p) => { setFormProtocol(p.key); setFormFastingH(p.fasting); setFormEatingH(p.eating); };

  const formatTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    if (h > 0) return `${h}h${m > 0 ? m.toString().padStart(2, '0') : ''}`;
    return `${m}m`;
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  }

  const status = localStatus || {};
  const isFasting = status.in_fasting_window;
  const phase = status.phase || 'eating';
  const progress = status.progress_pct || 0;
  const remaining = status.time_remaining_minutes || 0;
  const currentStreak = streak?.streak || 0;
  const history = streak?.history || [];
  const isActive = settings?.is_active && status?.is_active;

  return (
    <Layout>
    <div className="max-w-lg mx-auto pb-24" data-testid="fasting-page">

      {/* ═══ HERO SECTION ═══ */}
      <div className="relative rounded-b-3xl overflow-hidden mb-6">
        <img src={HERO_IMG} alt="" className="w-full h-48 sm:h-56 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/30 to-amber-900/20" />
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-end justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-heading uppercase tracking-wider text-white flex items-center gap-2.5">
                <Timer className="w-6 h-6 text-amber-400" />
                {t('pages.fasting_title')}
              </h1>
              <p className="text-xs text-white/60 mt-1 max-w-xs">
                {t('pages.fasting_subtitle')}
              </p>
            </div>
            <button onClick={() => setShowSettings(true)} className="p-2.5 rounded-xl bg-white/10 backdrop-blur-md text-white/80 hover:text-white hover:bg-white/20 transition-all border border-white/10" data-testid="fasting-page-settings-btn">
              <Settings className="w-4.5 h-4.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 space-y-5">

        {/* ═══ NOT ACTIVE — WARM CTA ═══ */}
        {!isActive && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-0 bg-gradient-to-br from-indigo-500/10 via-card to-amber-500/5 shadow-lg shadow-indigo-500/5" data-testid="fasting-activate-card">
              <CardContent className="p-7 text-center space-y-5">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-amber-500/10 flex items-center justify-center mx-auto border border-indigo-500/20">
                  <Timer className="w-10 h-10 text-indigo-400" />
                </div>
                <div>
                  <h2 className="text-base font-heading uppercase tracking-wide">{t('pages.fasting_ready')}</h2>
                  <p className="text-xs text-muted-foreground mt-2 leading-relaxed max-w-xs mx-auto">
                    {fr
                      ? 'Le jeûne intermittent booste ton énergie, ta clarté mentale et ta composition corporelle. Coach YUMIX s\'adaptera automatiquement à ta fenêtre alimentaire.'
                      : 'Intermittent fasting boosts your energy, mental clarity, and body composition. Coach YUMIX will automatically adapt to your eating window.'}
                  </p>
                </div>
                <Button onClick={() => { setFormActive(true); setShowSettings(true); }} className="bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white shadow-lg shadow-indigo-500/20 px-8" data-testid="fasting-activate-btn">
                  <Zap className="w-4 h-4 mr-2" /> {fr ? 'Commencer mon jeûne' : 'Start my fast'}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* ═══ ACTIVE TRACKER ═══ */}
        {isActive && (
          <>
            {/* Timer Card */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <Card className={`border-0 shadow-lg ${isFasting ? 'bg-gradient-to-br from-indigo-500/8 via-card to-violet-500/5 shadow-indigo-500/10' : 'bg-gradient-to-br from-emerald-500/8 via-card to-teal-500/5 shadow-emerald-500/10'}`} data-testid="fasting-main-tracker">
                <CardContent className="p-6">
                  <div className="flex flex-col items-center gap-4">
                    <div className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-wider uppercase ${
                      isFasting ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/20' : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/20'
                    }`}>
                      {isFasting ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
                      {isFasting ? (fr ? 'Phase de jeûne' : 'Fasting') : (fr ? 'Fenêtre alimentaire' : 'Eating window')}
                      <span className="text-[9px] opacity-60 ml-1">{settings.protocol}</span>
                    </div>

                    <CircularProgress progress={progress} size={190} strokeWidth={10} phase={phase} timeText={formatTime(remaining)} subText={fr ? 'restant' : 'remaining'} />

                    <div className="w-full max-w-xs">
                      <div className="flex justify-between text-[9px] text-muted-foreground mb-1.5">
                        <span>{fr ? 'Debut' : 'Start'}</span>
                        <span className="font-bold text-foreground/70">{Math.round(progress)}%</span>
                        <span>{fr ? 'Fin' : 'End'}</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <motion.div className={`h-full rounded-full ${isFasting ? 'bg-gradient-to-r from-indigo-600 to-violet-500' : 'bg-gradient-to-r from-emerald-600 to-teal-500'}`} initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.8 }} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Streak + Tips */}
            <div className="grid grid-cols-2 gap-3">
              <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                <Card className={`border-0 h-full ${currentStreak > 0 ? 'bg-gradient-to-br from-orange-500/10 to-amber-500/5 shadow-orange-500/5' : 'bg-card/60'} shadow-lg`} data-testid="fasting-streak-card">
                  <CardContent className="p-4 text-center flex flex-col justify-center h-full">
                    <div className="flex items-center justify-center gap-1.5 mb-1">
                      <Flame className={`w-6 h-6 ${currentStreak > 0 ? 'text-orange-400' : 'text-muted-foreground/20'}`} />
                      <span className="text-3xl font-black">{currentStreak}</span>
                    </div>
                    <p className="text-[9px] text-muted-foreground uppercase tracking-widest">
                      {fr ? 'Jours consecutifs' : 'Day streak'}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}>
                <Card className="bg-card/60 border-0 h-full shadow-lg">
                  <CardContent className="p-4 space-y-2">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-widest font-bold mb-2">{isFasting ? (fr ? 'Permis' : 'Allowed') : (fr ? 'Conseils' : 'Tips')}</p>
                    {isFasting ? (
                      <>
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><Droplets className="w-3.5 h-3.5 text-blue-400 shrink-0" /> {fr ? 'Eau, café noir, thé' : 'Water, black coffee, tea'}</div>
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><Coffee className="w-3.5 h-3.5 text-amber-400 shrink-0" /> {fr ? 'Electrolytes' : 'Electrolytes'}</div>
                        <p className="text-[9px] text-indigo-400/80 italic mt-1">{fr ? 'IA sync — aucun repas' : 'AI synced — no meals'}</p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><Sun className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {fr ? 'Protéines + legumes' : 'Protein + veggies'}</div>
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><Apple className="w-3.5 h-3.5 text-red-400 shrink-0" /> {fr ? 'Évite les sucres' : 'Avoid sugars'}</div>
                        <p className="text-[9px] text-emerald-400/80 italic mt-1">{fr ? 'IA — repas optimaux' : 'AI — optimal meals'}</p>
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* 7-Day History */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="bg-card/60 border-0 shadow-lg" data-testid="fasting-history">
                <CardContent className="p-4">
                  <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground flex items-center gap-2 mb-3">
                    <CalendarDays className="w-3.5 h-3.5 text-indigo-400" />
                    {fr ? 'Historique (7 jours)' : 'History (7 days)'}
                  </p>
                  <div className="flex items-end justify-between gap-1.5">
                    {history.length > 0 ? history.slice(-7).map((day, i) => {
                      const dayLabel = new Date(day.date).toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { weekday: 'narrow' });
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                          <div className="w-full relative" style={{ height: 50 }}>
                            <div className="absolute bottom-0 w-full bg-white/[0.03] rounded-md" style={{ height: 50 }} />
                            <motion.div className={`absolute bottom-0 w-full rounded-md ${day.completed ? 'bg-gradient-to-t from-indigo-600 to-violet-500' : 'bg-indigo-500/20'}`} initial={{ height: 0 }} animate={{ height: `${Math.min(100, day.pct || 0)}%` }} transition={{ duration: 0.6, delay: i * 0.06 }} style={{ maxHeight: 50 }} />
                          </div>
                          <span className="text-[9px] text-muted-foreground font-medium">{dayLabel}</span>
                        </div>
                      );
                    }) : (
                      <p className="text-[10px] text-muted-foreground text-center w-full py-4">{fr ? 'L\'historique apparaîtra après ton premier jeûne.' : 'History appears after your first fast.'}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </>
        )}

        {/* ═══ L'ACADÉMIE DU JEÛNE — LE GUIDE TACTIQUE ═══ */}
        <div className="pt-2">
          <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1 px-1 flex items-center gap-2">
            <Timer className="w-3.5 h-3.5 text-indigo-400" />
            {fr ? "L'Académie du Jeûne" : 'Fasting Academy'}
          </p>
          <p className="text-[9px] text-muted-foreground/60 mb-3 px-1">
            {fr ? "Le jeûne n'est pas un régime, c'est un outil métabolique." : "Fasting isn't a diet, it's a metabolic tool."}
          </p>
          <div className="space-y-2.5" data-testid="fasting-education-section">

            {/* 1. COMMENT CA FONCTIONNE */}
            <AccordionItem icon={Zap} iconColor="bg-amber-500/15 text-amber-400" title={fr ? "Comment ça fonctionne?" : "How does it work?"} defaultOpen={!isActive} testId="edu-how-it-works">
              <div className="space-y-3">
                <p>{fr
                  ? "Le jeûne intermittent est une stratégie qui consiste à diviser votre journée en deux fenêtres : une fenêtre où vous mangez, et une fenêtre où vous laissez votre corps se reposer et se réparer."
                  : "Intermittent fasting divides your day into two windows: an eating window and a rest-and-repair window."
                }</p>
                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/15">
                  <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider mb-2">{fr ? 'La mécanique' : 'The mechanics'}</p>
                  <p className="text-xs leading-relaxed">{fr
                    ? "Votre corps fonctionne avec deux types de carburant : le sucre (glucose) et le gras (lipides). Quand vous mangez du matin au soir, votre taux d'insuline reste élevé. Votre corps brûle le sucre de vos repas et n'a jamais besoin de puiser dans ses réserves de gras."
                    : "Your body runs on two fuel types: sugar (glucose) and fat (lipids). When you eat from morning to night, insulin stays high and your body never needs to tap into fat reserves."
                  }</p>
                  <p className="text-xs leading-relaxed mt-2 font-semibold text-amber-300/80">{fr
                    ? "Le jeûne force l'insuline à baisser, ce qui ouvre enfin les portes de vos réserves de graisse pour les utiliser comme énergie."
                    : "Fasting forces insulin down, finally unlocking your fat reserves for energy."
                  }</p>
                </div>
              </div>
            </AccordionItem>

            {/* 2. LES IMPACTS MAJEURS */}
            <AccordionItem icon={HeartPulse} iconColor="bg-rose-500/15 text-rose-400" title={fr ? "Les impacts majeurs" : "Major impacts"} testId="edu-impacts">
              <div className="space-y-2.5 mt-1">
                {[
                  { icon: Flame, color: 'text-orange-400', bg: 'bg-orange-500/10', title: fr ? 'Perte de gras ciblée' : 'Targeted fat loss', desc: fr ? 'Votre corps devient une machine à brûler les graisses au lieu de dépendre du sucre.' : 'Your body becomes a fat-burning machine instead of depending on sugar.' },
                  { icon: Brain, color: 'text-violet-400', bg: 'bg-violet-500/10', title: fr ? 'Clarté mentale' : 'Mental clarity', desc: fr ? "La digestion demande énormément d'énergie. En jeûnant, cette énergie monte au cerveau. Fini le brouillard de l'après-midi." : "Digestion uses massive energy. While fasting, that energy goes to your brain." },
                  { icon: ShieldCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10', title: fr ? 'Repos digestif' : 'Digestive rest', desc: fr ? 'Vos organes (foie, pancréas, intestins) prennent enfin des vacances pour guérir et réduire l\'inflammation globale.' : 'Your organs finally take a break to heal and reduce inflammation.' },
                  { icon: HeartPulse, color: 'text-rose-400', bg: 'bg-rose-500/10', title: fr ? 'Sensibilité à l\'insuline' : 'Insulin sensitivity', desc: fr ? 'Excellent pour la prévention du diabète et la gestion des baisses d\'énergie.' : 'Excellent for diabetes prevention and energy management.' },
                ].map((item, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 rounded-xl ${item.bg} border border-white/[0.04]`}>
                    <item.icon className={`w-5 h-5 ${item.color} shrink-0 mt-0.5`} />
                    <div>
                      <p className="text-[11px] font-bold text-foreground">{item.title}</p>
                      <p className="text-[10px] text-muted-foreground leading-relaxed mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </AccordionItem>

            {/* 3. LA LIGNE DU TEMPS — Heure par heure */}
            <AccordionItem icon={Timer} iconColor="bg-indigo-500/15 text-indigo-400" title={fr ? "La Ligne du Temps" : "The Timeline"} testId="edu-timeline">
              <div className="space-y-0 mt-1">
                <p className="text-[10px] text-muted-foreground mb-3">{fr ? "Votre corps traverse 4 phases distinctes pendant un jeûne. Voici votre plan de vol :" : "Your body goes through 4 distinct phases during a fast:"}</p>

                {/* Phase 1 */}
                <div className="relative pl-5 pb-4 border-l-2 border-zinc-700">
                  <div className="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-blue-400" />
                  <p className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">Phase 1 — {fr ? 'L\'Absorption' : 'Absorption'}</p>
                  <p className="text-[9px] text-muted-foreground/60 font-mono">H0 — H4</p>
                  <p className="text-[10px] text-muted-foreground leading-relaxed mt-1">{fr
                    ? "Votre corps digère votre dernier repas. L'insuline est haute, votre corps stocke l'énergie. Vous carburez au sucre de ce que vous venez de manger."
                    : "Your body digests your last meal. Insulin is high, storing energy. You're running on sugar from your meal."
                  }</p>
                </div>

                {/* Phase 2 */}
                <div className="relative pl-5 pb-4 border-l-2 border-zinc-700">
                  <div className="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-amber-400" />
                  <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Phase 2 — {fr ? 'La Transition' : 'Transition'}</p>
                  <p className="text-[9px] text-muted-foreground/60 font-mono">H4 — H12</p>
                  <p className="text-[10px] text-muted-foreground leading-relaxed mt-1">{fr
                    ? "La digestion est terminée. Votre taux d'insuline chute. Votre corps a épuisé le sucre du dernier repas et commence à vider ses réserves de glycogène. C'est souvent ici que l'estomac gargouille : buvez de l'eau!"
                    : "Digestion is complete. Insulin drops. Your body depletes the last meal's sugar and starts emptying glycogen reserves. This is when stomach grumbles happen — drink water!"
                  }</p>
                </div>

                {/* Phase 3 */}
                <div className="relative pl-5 pb-4 border-l-2 border-zinc-700">
                  <div className="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-orange-500" />
                  <p className="text-[10px] font-bold text-orange-400 uppercase tracking-wider">Phase 3 — {fr ? 'La Machine à Gras' : 'Fat-Burning Machine'}</p>
                  <p className="text-[9px] text-muted-foreground/60 font-mono">H12 — H16</p>
                  <p className="text-[10px] text-muted-foreground leading-relaxed mt-1">{fr
                    ? "C'est ici que la magie opère. Vos réserves de sucre sont vides. Votre corps libère des hormones de croissance et commence à casser vos cellules graisseuses pour les transformer en énergie (corps cétoniques). Vous brûlez officiellement du gras. Votre énergie remonte en flèche."
                    : "This is where the magic happens. Sugar reserves are empty. Your body releases growth hormones and starts breaking down fat cells into ketone bodies. You're officially burning fat. Energy skyrockets."
                  }</p>
                </div>

                {/* Phase 4 */}
                <div className="relative pl-5 pb-1">
                  <div className="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-purple-500" />
                  <p className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">Phase 4 — {fr ? 'Le Grand Nettoyage' : 'The Deep Clean'}</p>
                  <p className="text-[9px] text-muted-foreground/60 font-mono">H16+</p>
                  <p className="text-[10px] text-muted-foreground leading-relaxed mt-1">{fr
                    ? "Bienvenue dans l'Autophagie. Votre corps, n'ayant rien à digérer, se met en mode réparation clinique. Il détruit les vieilles cellules mortes, les protéines endommagées et les toxines pour créer de nouvelles cellules saines. C'est le niveau élite de la récupération cellulaire et de l'anti-âge."
                    : "Welcome to Autophagy. With nothing to digest, your body enters clinical repair mode. It destroys old cells, damaged proteins, and toxins to create new healthy cells. This is elite-level cell recovery and anti-aging."
                  }</p>
                </div>
              </div>
            </AccordionItem>

            {/* 4. LE KIT DE SURVIE */}
            <AccordionItem icon={ShieldCheck} iconColor="bg-emerald-500/15 text-emerald-400" title={fr ? "Le Kit de Survie" : "Survival Kit"} testId="edu-rules">
              <div className="space-y-3">
                <p className="text-[10px] text-muted-foreground">{fr
                  ? "Qu'est-ce qui brise le jeûne? Tout ce qui contient des calories ou déclenche l'insuline."
                  : "What breaks the fast? Anything with calories or that triggers insulin."
                }</p>
                <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/15">
                  <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider mb-2">{fr ? 'PERMIS (à volonté)' : 'ALLOWED (unlimited)'}</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs"><GlassWater className="w-3.5 h-3.5 text-blue-400 shrink-0" /> {fr ? 'Eau plate ou petillante (votre meilleure arme)' : 'Still or sparkling water (your best weapon)'}</div>
                    <div className="flex items-center gap-2 text-xs"><Coffee className="w-3.5 h-3.5 text-amber-400 shrink-0" /> {fr ? 'Café noir (sans lait, sans crème, sans sucre, sans édulcorant)' : 'Black coffee (no milk, no cream, no sugar, no sweetener)'}</div>
                    <div className="flex items-center gap-2 text-xs"><Droplets className="w-3.5 h-3.5 text-cyan-400 shrink-0" /> {fr ? 'Thé vert, thé noir, tisanes (sans aucun ajout)' : 'Green tea, black tea, herbal tea (nothing added)'}</div>
                    <div className="flex items-center gap-2 text-xs"><Zap className="w-3.5 h-3.5 text-yellow-400 shrink-0" /> {fr ? "Pincée de sel rose ou électrolytes purs dans l'eau" : 'Pinch of pink salt or pure electrolytes in water'}</div>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/15">
                  <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-2">{fr ? 'INTERDIT (brise le jeûne)' : 'FORBIDDEN (breaks the fast)'}</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs"><BanIcon className="w-3.5 h-3.5 text-red-400 shrink-0" /> {fr ? 'Jus, sodas, lait, alcool' : 'Juice, soda, milk, alcohol'}</div>
                    <div className="flex items-center gap-2 text-xs"><BanIcon className="w-3.5 h-3.5 text-red-400 shrink-0" /> {fr ? 'Sucre, miel, sirop' : 'Sugar, honey, syrup'}</div>
                    <div className="flex items-center gap-2 text-xs"><BanIcon className="w-3.5 h-3.5 text-red-400 shrink-0" /> {fr ? 'Compléments alimentaires sucrés (gummies)' : 'Sweetened supplements (gummies)'}</div>
                  </div>
                </div>
                <div className="mt-2 p-3 rounded-lg bg-indigo-500/5 border border-indigo-500/15">
                  <p className="text-[10px] font-semibold text-indigo-300 italic leading-relaxed">{fr
                    ? "\"Le jeûne est un muscle. La première semaine est toujours la plus difficile car votre corps est habitué au sucre. Soyez patients, hydratez-vous, et regardez votre corps se transformer. ALL IN!\""
                    : "\"Fasting is a muscle. The first week is always the hardest because your body is used to sugar. Be patient, stay hydrated, and watch your body transform. ALL IN!\""
                  }</p>
                </div>
              </div>
            </AccordionItem>

          </div>
        </div>

      </div>

      {/* ═══ SETTINGS MODAL ═══ */}
      <AnimatePresence>
        {showSettings && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setShowSettings(false)}>
            <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }} transition={{ type: 'spring', damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card border border-border/50 rounded-2xl p-5 w-full max-w-sm space-y-4 shadow-2xl max-h-[85vh] overflow-y-auto"
              data-testid="fasting-page-settings-modal">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-heading uppercase tracking-wider flex items-center gap-2">
                  <Timer className="w-4 h-4 text-indigo-400" /> {fr ? 'Configuration' : 'Settings'}
                </h3>
                <button onClick={() => setShowSettings(false)} className="p-1 rounded-lg hover:bg-secondary"><X className="w-4 h-4 text-muted-foreground" /></button>
              </div>

              <div>
                <label className="text-[10px] text-muted-foreground uppercase tracking-widest mb-2 block">{fr ? 'Choisis ton protocole' : 'Choose your protocol'}</label>
                <div className="grid grid-cols-2 gap-2">
                  {PROTOCOLS.map(p => (
                    <button key={p.key} onClick={() => handleProtocolSelect(p)} data-testid={`page-protocol-${p.key}`}
                      className={`p-3 rounded-xl border text-center transition-all ${formProtocol === p.key ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-300 shadow-lg shadow-indigo-500/5' : 'bg-white/[0.02] border-border/50 text-muted-foreground hover:border-indigo-500/20'}`}>
                      <span className="text-base font-black block">{p.label}</span>
                      <span className="text-[9px] block mt-0.5 opacity-60">{p.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-1 block">{fr ? 'Heures jeûne' : 'Fast hours'}</label>
                  <Input type="number" min={10} max={23} value={formFastingH}
                    onChange={(e) => { const v = parseInt(e.target.value) || 16; setFormFastingH(v); setFormEatingH(24 - v); setFormProtocol('custom'); }}
                    className="text-center font-bold" data-testid="page-fasting-hours" />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase mb-1 block">{fr ? 'Fenêtre alim.' : 'Eat window'}</label>
                  <Input type="number" min={1} max={14} value={formEatingH}
                    onChange={(e) => { const v = parseInt(e.target.value) || 8; setFormEatingH(v); setFormFastingH(24 - v); setFormProtocol('custom'); }}
                    className="text-center font-bold" data-testid="page-eating-hours" />
                </div>
              </div>

              <div>
                <label className="text-[10px] text-muted-foreground uppercase mb-1 block">{fr ? 'Heure de début du jeûne' : 'Fasting start time'}</label>
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} max={23} value={formStartHour} onChange={(e) => setFormStartHour(parseInt(e.target.value) || 0)} className="w-20 text-center font-bold" data-testid="page-start-hour" />
                  <span className="text-lg font-bold text-muted-foreground">:</span>
                  <Input type="number" min={0} max={59} step={15} value={formStartMin} onChange={(e) => setFormStartMin(parseInt(e.target.value) || 0)} className="w-20 text-center font-bold" data-testid="page-start-min" />
                </div>
              </div>

              <label className={`flex items-center gap-3 cursor-pointer p-3 rounded-xl border transition-all ${formActive ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-white/[0.02] border-border/50'}`}>
                <input type="checkbox" checked={formActive} onChange={(e) => setFormActive(e.target.checked)} className="sr-only" />
                <div className={`w-10 h-5 rounded-full transition-all relative ${formActive ? 'bg-indigo-500' : 'bg-zinc-700'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${formActive ? 'left-5' : 'left-0.5'}`} />
                </div>
                <span className="text-xs font-bold">{fr ? 'Activer le suivi' : 'Activate tracking'}</span>
              </label>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setShowSettings(false)} className="flex-1">{fr ? 'Annuler' : 'Cancel'}</Button>
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white" data-testid="page-fasting-save">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                  {fr ? 'Sauvegarder' : 'Save'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
    </Layout>
  );
}
